import { useMemo, useRef } from "react";
// react-joyride v3 — named export. v3 reworked the API vs v2:
//   - callback  → onEvent(data, controls)
//   - colours / behaviour live in `options` (not styles.options)
//   - a custom tooltip is provided via `tooltipComponent`
//   - uncontrolled mode (no stepIndex) lets Joyride manage step advancement
import { Joyride, EVENTS, STATUS } from "react-joyride";

// ── Generic guide runner ──────────────────────────────────────────────────
// Runs any guide (tour or coachmark) and reports how it ended:
//   onComplete() — user finished all steps
//   onSkip()     — user skipped / dismissed
//
// Robustness: before running we drop any step whose target isn't currently in
// the DOM, so the tour never stalls on a missing element (which previously
// left the overlay up and the page unclickable).

const OPTIONS = {
  primaryColor: "#7B5AC4",
  textColor: "#1E293B",
  backgroundColor: "#FFFFFF",
  arrowColor: "#FFFFFF",
  overlayColor: "rgba(15, 23, 42, 0.5)",
  zIndex: 9999,
  width: 360,
  // Open the tooltip immediately instead of a beacon the user must click.
  skipBeacon: true,
  // The close (×) button ends the whole tour.
  closeButtonAction: "skip",
};

// ── Custom tooltip card: header · details · footer ─────────────────────────
function GuideTooltip({
  step,
  index,
  size,
  isLastStep,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
}) {
  const pct = size > 0 ? Math.round(((index + 1) / size) * 100) : 100;
  return (
    <div {...tooltipProps} style={card}>
      {/* Header */}
      <div style={header}>
        <div style={{ minWidth: 0 }}>
          <div style={stepLabel}>
            Step {index + 1} of {size}
          </div>
          {step.title && <div style={title}>{step.title}</div>}
        </div>
        <button {...closeProps} style={closeBtn}>
          ×
        </button>
      </div>

      {/* Progress */}
      <div style={progressTrack}>
        <div style={{ ...progressFill, width: `${pct}%` }} />
      </div>

      {/* Details */}
      <div style={body}>{step.content}</div>

      {/* Footer */}
      <div style={footer}>
        <button {...skipProps} style={skipBtn}>
          Skip tour
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          {index > 0 && (
            <button {...backProps} style={backBtn}>
              Back
            </button>
          )}
          <button {...primaryProps} style={primaryBtn}>
            {isLastStep ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function JoyrideRunner({ guide, onComplete, onSkip }) {
  const endedRef = useRef(false);
  const advancedRef = useRef(new Set());

  // Drop steps whose target isn't on the page right now. Memoised per guide so
  // the steps array stays stable for the tour's lifetime. Falls back to the
  // full list if nothing matches (e.g. a target that mounts a beat later).
  const steps = useMemo(() => {
    if (!guide) return [];
    endedRef.current = false;
    advancedRef.current = new Set();
    const present = guide.steps.filter((s) => {
      try {
        return !!document.querySelector(s.target);
      } catch {
        return false;
      }
    });
    return present.length ? present : guide.steps;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guide?.id]);

  if (!guide || steps.length === 0) return null;

  const end = (fn) => {
    if (endedRef.current) return;
    endedRef.current = true;
    fn?.();
  };

  const handleEvent = (data, controls) => {
    const { type, status, index, size } = data;

    // Missing target: advance past it once. If it re-fires for the same step
    // (would loop) or it's the last step, stop cleanly instead of freezing.
    if (type === EVENTS.TARGET_NOT_FOUND) {
      const isLast =
        typeof index === "number" && typeof size === "number" && index >= size - 1;
      if (isLast || advancedRef.current.has(index)) {
        controls.stop();
        end(onComplete);
      } else {
        advancedRef.current.add(index);
        controls.next();
      }
      return;
    }

    if (status === STATUS.SKIPPED) {
      end(onSkip);
      return;
    }
    if (type === EVENTS.TOUR_END || status === STATUS.FINISHED) {
      end(onComplete);
    }
  };

  return (
    <Joyride
      key={guide.id}
      steps={steps}
      run
      continuous
      onEvent={handleEvent}
      tooltipComponent={GuideTooltip}
      options={OPTIONS}
    />
  );
}

// ── styles ─────────────────────────────────────────────────────────────────
const card = {
  width: 360,
  maxWidth: "calc(100vw - 32px)",
  background: "white",
  borderRadius: 12,
  boxShadow: "0 20px 50px rgba(15,23,42,0.28)",
  overflow: "hidden",
  fontFamily: "'Inter', sans-serif",
};
const header = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 10,
  padding: "14px 16px 10px",
  background: "linear-gradient(135deg, #EEF0FF, #F8FAFF)",
};
const stepLabel = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.5,
  textTransform: "uppercase",
  color: "#7B5AC4",
};
const title = {
  fontSize: 15,
  fontWeight: 700,
  color: "#0F172A",
  marginTop: 3,
};
const closeBtn = {
  border: "none",
  background: "transparent",
  fontSize: 22,
  lineHeight: 1,
  color: "#94A3B8",
  cursor: "pointer",
  flexShrink: 0,
};
const progressTrack = { height: 3, background: "#EEF0FF" };
const progressFill = {
  height: "100%",
  background: "#7B5AC4",
  transition: "width 0.25s ease",
};
const body = {
  padding: "12px 16px 14px",
  fontSize: 13,
  lineHeight: 1.55,
  color: "#475569",
};
const footer = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "10px 16px",
  borderTop: "1px solid #F1F5F9",
  background: "#FCFCFD",
};
const skipBtn = {
  border: "none",
  background: "transparent",
  color: "#94A3B8",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  padding: "4px 2px",
};
const backBtn = {
  padding: "7px 14px",
  borderRadius: 7,
  background: "white",
  color: "#475569",
  border: "1px solid #E2E8F0",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
const primaryBtn = {
  padding: "7px 16px",
  borderRadius: 7,
  background: "#7B5AC4",
  color: "white",
  border: "none",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
